import BillingNotifications from '@/components/BillingNotifications'
import ConsoleTools from '@/components/ConsoleTools'
import { ChevronDown } from 'lucide-react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { authApi } from '@/api/auth'
import { errorMessage } from '@/api/client'
import { useAuthStore } from '@/store/auth'

const roleNames = {
  super_admin: '超级管理员',
  finance: '财务',
  operator: '运营',
}
export default function Layout({ children, preview = false, prototypeMode = false }: { children: ReactNode; preview?: boolean; prototypeMode?: boolean }) {
  const { admin, logout } = useAuthStore()
  const cache = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const isModelCostsRoute = ['/model-costs', '/quotes', '/prototype/model-costs', '/prototype/model-costs/quotes'].includes(location.pathname)
  const isCopyrightRoute = location.pathname.startsWith('/copyright-trading/')
  const [copyrightOpen, setCopyrightOpen] = useState(isCopyrightRoute)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (isCopyrightRoute) setCopyrightOpen(true)
  }, [isCopyrightRoute])
  async function signOut() {
    setBusy(true)
    setError('')
    try {
      await authApi.logout()
      logout()
      cache.clear()
      navigate('/login', { replace: true })
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 text-slate-900 lg:h-dvh lg:overflow-hidden">
      <header className="shrink-0 border-b border-slate-200 bg-white px-5 py-4 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <NavLink to="/dashboard" onClick={e => { if (preview) e.preventDefault() }} className="text-lg font-semibold">PPNew 管理后台</NavLink>
            <p className="text-sm font-normal tracking-wide text-slate-400 sm:border-l sm:border-slate-200 sm:pl-6">
              让每一份创作，都有从容的守护
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {!prototypeMode && <ConsoleTools inline />}
            <span className="whitespace-nowrap">
              {preview ? '本地演示环境' : <>{admin?.username} · {admin && roleNames[admin.role]}</>}
            </span>
            {!prototypeMode ? <button
              className="text-brand-600 disabled:opacity-50"
              disabled={busy || preview}
              onClick={signOut}
            >
              {busy ? '退出中…' : '退出登录'}
            </button> : <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700">交互原型</span>}
          </div>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <nav
          className="flex shrink-0 gap-2 overflow-x-auto border-b border-slate-200 bg-white p-3 lg:w-52 lg:flex-col lg:border-b-0 lg:border-r lg:p-4"
          aria-label="主导航"
        >
          {[
            ['/dashboard', '概览'],
            ['/users', '用户管理'],
            ['/teams', '团队管理'],
            ['/face-auth', '人脸授权管理'],
            ['/recharge-analytics', '充值数据统计'],
            ...((admin?.role === 'finance' || admin?.role === 'super_admin' || prototypeMode) ? [['/model-costs', '模型成本与报价']] : []),
            ...(admin?.role !== 'operator' ? [['/packages', '套餐管理'], ['/orders', '订单管理'], ['/recharges', '充值记录']] : []),
            ...(admin?.role === 'super_admin'
              ? [['/audit-logs', '操作记录']]
              : []),
          ].map(([to, label]) => (
            <NavLink
              key={to}
              to={prototypeMode && location.pathname.startsWith('/prototype/model-costs') && to === '/model-costs' ? '/prototype/model-costs' : to}
              end={to === '/model-costs'}
              onClick={e => { if (preview) e.preventDefault() }}
              aria-disabled={preview}
              className={({ isActive }) =>
                'whitespace-nowrap rounded-xl px-4 py-3 text-sm font-medium transition ' +
                (isActive || (to === '/model-costs' && isModelCostsRoute) || (preview && to === (location.pathname === '/prototype/packages' ? '/packages' : '/recharge-analytics'))
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100')
              }
            >
              {label}
            </NavLink>
          ))}
          {!preview && <div className="flex shrink-0 flex-col gap-1" role="group" aria-label="版权交易管理">
            <button
              type="button"
              aria-expanded={copyrightOpen}
              aria-controls="copyright-trading-subnav"
              onClick={() => setCopyrightOpen((open) => !open)}
              className={'flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-xl px-4 py-3 text-left text-sm font-medium transition ' +
                (isCopyrightRoute
                  ? 'bg-brand-50 text-brand-700'
                  : 'bg-white text-slate-600 hover:bg-slate-100')}
            >
              版权交易管理
              <ChevronDown size={16} className={copyrightOpen ? 'rotate-180' : ''} aria-hidden="true" />
            </button>
            <div id="copyright-trading-subnav" className={(copyrightOpen ? 'flex' : 'hidden') + ' flex-col border-l border-slate-200 pl-2 lg:ml-4'}>
                <NavLink
                  to="/copyright-trading/production-commissions"
                  className={({ isActive }) =>
                    'whitespace-nowrap rounded-lg px-3 py-2 text-sm transition ' +
                    (isActive
                      ? 'bg-brand-600 font-medium text-white'
                      : 'text-slate-600 hover:bg-slate-100')
                  }
                >
                  漫剧承制管理
                </NavLink>
            </div>
          </div>}
          {!preview && !prototypeMode && admin && admin.role !== 'operator' && <BillingNotifications key={admin.id} />}
        </nav>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4 sm:p-6 lg:p-8">
          {error && (
            <p role="alert" className="mb-4 text-red-700">
              {error}
            </p>
          )}
          <main className="page-scene flex min-h-0 min-w-0 flex-1 flex-col">
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
